import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import {
  PageHeader, Button, Card, Badge, Modal, Input, EmptyState, formatDate,
} from '../components/UI';

export default function Patients({ params }) {
  const [list, setList]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // patient object | null
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);    // detailed patient view

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (params?.id) openDetail(params.id);
  }, [params]);

  async function refresh() {
    setLoading(true);
    try { setList(await api.patients.list()); }
    catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }

  async function openDetail(id) {
    try { setDetail(await api.patients.get(id)); }
    catch (e) { alert(e.message); }
  }

  return (
    <>
      <PageHeader
        eyebrow="Gestión"
        title="Pacientes"
        subtitle={`${list.length} pacientes registrados`}
        actions={
          <Button onClick={() => setCreating(true)}>+ Nuevo paciente</Button>
        }
      />

      {loading ? (
        <EmptyState>Cargando…</EmptyState>
      ) : list.length === 0 ? (
        <EmptyState>No hay pacientes registrados todavía.</EmptyState>
      ) : (
        <Card padding={0}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <Th>Nombre</Th>
                <Th>Email</Th>
                <Th>Teléfono</Th>
                <Th>Suscripción</Th>
                <Th>Audiciones</Th>
                <Th>Estado</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {list.map(p => (
                <tr key={p.id} style={trStyle}>
                  <Td><div style={{ fontWeight: 400 }}>{p.name}</div></Td>
                  <Td muted>{p.email}</Td>
                  <Td muted>{p.phone || '—'}</Td>
                  <Td>
                    {!p.end_date ? <Badge>Sin sub.</Badge> :
                     p.state === 'active' ? <Badge tone="success">{formatDate(p.end_date)}</Badge> :
                     p.state === 'expiring_soon' ? <Badge tone="warning">{p.days_left}d → {formatDate(p.end_date)}</Badge> :
                     <Badge tone="danger">Vencida {formatDate(p.end_date)}</Badge>}
                  </Td>
                  <Td muted>{p.total_plays || 0}</Td>
                  <Td>
                    {p.is_active ? <Badge tone="success">Activo</Badge> : <Badge tone="neutral">Inactivo</Badge>}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button size="sm" variant="ghost" onClick={() => openDetail(p.id)}>Ver</Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditing(p)}>Editar</Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal crear/editar */}
      {(creating || editing) && (
        <PatientForm
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}

      {/* Modal detalle */}
      {detail && (
        <PatientDetail
          data={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { refresh(); openDetail(detail.patient.id); }}
        />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────
function PatientForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName]         = useState(initial?.name || '');
  const [email, setEmail]       = useState(initial?.email || '');
  const [phone, setPhone]       = useState(initial?.phone || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');

  async function submit(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      if (isEdit) {
        const data = { name, email, phone };
        if (password) data.password = password;
        await api.patients.update(initial.id, data);
      } else {
        await api.patients.create({ name, email, phone, password });
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar paciente' : 'Nuevo paciente'}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Input label="Nombre" value={name} onChange={e => setName(e.target.value)} required />
        <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        <Input label="Teléfono" value={phone} onChange={e => setPhone(e.target.value)} placeholder="opcional" />
        <Input
          label={isEdit ? 'Nueva contraseña' : 'Contraseña'}
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required={!isEdit}
          placeholder={isEdit ? 'dejar vacío para mantener' : 'mínimo 6 caracteres'}
        />
        {err && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────
function PatientDetail({ data, onClose, onChanged }) {
  const { patient, subscriptions, plays } = data;
  const [showNewSub, setShowNewSub] = useState(false);

  async function deactivate() {
    if (!window.confirm(`¿Desactivar a ${patient.name}? No podrá ingresar a la app.`)) return;
    try {
      await api.patients.deactivate(patient.id);
      onClose();
      onChanged();
    } catch (e) { alert(e.message); }
  }

  return (
    <Modal open onClose={onClose} title={patient.name} maxWidth={680}>
      <div style={{ marginBottom: 24 }}>
        <Row label="Email" value={patient.email} />
        <Row label="Teléfono" value={patient.phone || '—'} />
        <Row label="Estado" value={patient.is_active ? 'Activo' : 'Inactivo'} />
        <Row label="Registrado" value={formatDate(patient.created_at)} />
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <span>Suscripciones</span>
          <Button size="sm" onClick={() => setShowNewSub(true)}>+ Registrar pago</Button>
        </div>
        {subscriptions.length === 0 ? (
          <EmptyState>Sin suscripciones registradas</EmptyState>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <Th>Período</Th>
                <Th>Monto</Th>
                <Th>Método</Th>
                <Th>Ref.</Th>
                <Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <Td>{formatDate(s.start_date)} → {formatDate(s.end_date)}</Td>
                  <Td>{s.amount ? `$${parseFloat(s.amount).toLocaleString('es-AR')}` : '—'}</Td>
                  <Td muted>{s.payment_method}</Td>
                  <Td muted>{s.payment_ref || '—'}</Td>
                  <Td>
                    <Badge tone={s.status === 'active' ? 'success' : 'neutral'}>{s.status}</Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <span>Audiciones</span>
        </div>
        {plays.length === 0 ? (
          <EmptyState>No ha escuchado clases todavía</EmptyState>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <Th>Clase</Th>
                <Th>Audiciones</Th>
                <Th>Última vez</Th>
              </tr>
            </thead>
            <tbody>
              {plays.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <Td>
                    <div style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--earth)' }}>
                      {p.code}
                    </div>
                    <div>{p.title}</div>
                  </Td>
                  <Td>{p.play_count}</Td>
                  <Td muted>{formatDate(p.last_played)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
        <Button variant="danger" size="sm" onClick={deactivate}>Desactivar paciente</Button>
        <Button variant="secondary" onClick={onClose}>Cerrar</Button>
      </div>

      {showNewSub && (
        <NewSubscriptionForm
          patientId={patient.id}
          onClose={() => setShowNewSub(false)}
          onSaved={() => { setShowNewSub(false); onChanged(); }}
        />
      )}
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────
function NewSubscriptionForm({ patientId, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const inAMonth = new Date(); inAMonth.setMonth(inAMonth.getMonth() + 1);
  const defaultEnd = inAMonth.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate]     = useState(defaultEnd);
  const [amount, setAmount]       = useState('');
  const [method, setMethod]       = useState('transferencia');
  const [ref, setRef]             = useState('');
  const [notes, setNotes]         = useState('');
  const [busy, setBusy]           = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.subscriptions.create({
        patient_id: patientId,
        start_date: startDate,
        end_date: endDate,
        amount: amount || null,
        payment_method: method,
        payment_ref: ref || null,
        notes: notes || null,
      });
      onSaved();
    } catch (e) {
      alert(e.message);
    } finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="Registrar pago / suscripción">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Inicio" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
          <Input label="Vencimiento" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Monto" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="opcional" />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 9, fontWeight: 300, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--earth)' }}>
              Método
            </span>
            <select value={method} onChange={e => setMethod(e.target.value)} style={{
              background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
              padding: '8px 0', fontFamily: 'inherit', fontSize: 14, fontWeight: 300, outline: 'none', borderRadius: 0,
            }}>
              <option value="transferencia">Transferencia</option>
              <option value="efectivo">Efectivo</option>
              <option value="mercadopago">MercadoPago</option>
              <option value="otro">Otro</option>
            </select>
          </label>
        </div>
        <Input label="Referencia" value={ref} onChange={e => setRef(e.target.value)} placeholder="ej: número de transferencia" />
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 300, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--earth)' }}>
            Notas
          </span>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{
            background: 'transparent', border: '1px solid var(--border)', padding: 10,
            fontFamily: 'inherit', fontSize: 14, fontWeight: 300, outline: 'none', borderRadius: 4, resize: 'vertical',
          }}/>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Guardando…' : 'Registrar'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── helpers ────────────────────────────────────────────────
const trStyle = { borderBottom: '1px solid var(--border)' };
const sectionStyle = { marginBottom: 20 };
const sectionHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: 11, fontWeight: 300, letterSpacing: '0.18em',
  textTransform: 'uppercase', color: 'var(--earth)',
  marginBottom: 12, marginTop: 8,
};

const Th = ({ children }) => (
  <th style={{
    textAlign: 'left', padding: '10px 14px',
    fontSize: 9, fontWeight: 300, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: 'var(--earth)',
  }}>{children}</th>
);

const Td = ({ children, muted }) => (
  <td style={{
    padding: '10px 14px', fontSize: 13,
    color: muted ? 'var(--ink-soft)' : 'inherit',
  }}>{children}</td>
);

function Row({ label, value }) {
  return (
    <div style={{
      display: 'flex', padding: '6px 0', fontSize: 13,
      borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ width: 120, color: 'var(--ink-soft)' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
